import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, User, Eye, Tag } from "lucide-react";
import DOMPurify from "dompurify";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BlogPostPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(({ data }) => setPost(data))
      .catch(() => setPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-red-600 border-t-transparent rounded-full" /></div>;

  if (!post) return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <h1 className="text-4xl font-bold text-slate-200 mb-4">404</h1>
        <p className="text-slate-500 mb-6">Article not found</p>
        <Link to="/blog" className="text-red-600 font-medium hover:underline">Back to Blog</Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-white" data-testid="blog-post-page">
      {/* Hero */}
      {post.cover_image && (
        <div className="w-full h-[40vh] relative">
          <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        </div>
      )}

      {/* Content */}
      <article className="container-custom max-w-3xl py-12">
        <Link to="/blog" className="text-red-600 text-sm font-medium flex items-center gap-1 mb-6 hover:underline" data-testid="blog-back">
          <ArrowLeft className="w-4 h-4" /> Back to Blog
        </Link>

        <span className="bg-red-50 text-red-600 px-3 py-1 rounded-full text-sm font-medium">{post.category}</span>
        <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-4 mb-4 leading-tight">{post.title}</h1>

        <div className="flex items-center gap-4 text-sm text-slate-500 mb-8 pb-8 border-b border-slate-100">
          <span className="flex items-center gap-1"><User className="w-4 h-4" /> {post.author}</span>
          {post.published_at && <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {new Date(post.published_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span>}
          <span className="flex items-center gap-1"><Eye className="w-4 h-4" /> {post.views || 0} views</span>
        </div>

        {/* Article Body */}
        <div
          className="prose prose-slate prose-lg max-w-none prose-headings:text-slate-900 prose-a:text-red-600 prose-img:rounded-xl"
          dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content) }}
        />

        {/* Tags */}
        {post.tags && post.tags.length > 0 && (
          <div className="flex items-center gap-2 mt-10 pt-6 border-t border-slate-100">
            <Tag className="w-4 h-4 text-slate-400" />
            {post.tags.map((tag) => (
              <span key={tag} className="text-xs bg-slate-100 text-slate-600 px-3 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
